import React, { useEffect, useRef } from 'react';

/**
 * The reference master, embedded from YouTube, reporting its playback position
 * so the lyrics can follow along.
 *
 * A plain <iframe> gives you no clock — which is why the lyrics sat frozen on
 * the first line. The IFrame API does, but it has no currentTime *event*, so
 * the position has to be polled while the video is playing.
 */

const API_SRC = 'https://www.youtube.com/iframe_api';
let apiPromise = null;

function loadApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    // onYouTubeIframeAPIReady is a single global the API calls once. Chain any
    // existing handler rather than stomping it.
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous();
      resolve(window.YT);
    };
    const script = document.createElement('script');
    script.src = API_SRC;
    script.async = true;
    script.onerror = () => reject(new Error('YouTube player failed to load'));
    document.body.appendChild(script);
  });
  return apiPromise;
}

export default function YouTubeReference({ videoId, title, onTime }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const onTimeRef = useRef(onTime);

  useEffect(() => { onTimeRef.current = onTime; }, [onTime]);

  useEffect(() => {
    let cancelled = false;

    // YT.Player REPLACES the element it is handed. Handing it a React-rendered
    // node leaves React trying to remove a child that no longer exists at
    // unmount, so give it a node React never knew about.
    const mount = document.createElement('div');
    if (hostRef.current) hostRef.current.appendChild(mount);

    loadApi()
      .then((YT) => {
        if (cancelled || !hostRef.current) return;
        playerRef.current = new YT.Player(mount, {
          videoId,
          playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
          events: {
            onStateChange: (event) => {
              const playing = event.data === YT.PlayerState.PLAYING;
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
              if (!playing) return;
              // 4/sec is enough for a line-level highlight and costs nothing.
              pollRef.current = setInterval(() => {
                const player = playerRef.current;
                if (player && player.getCurrentTime && onTimeRef.current) {
                  onTimeRef.current(player.getCurrentTime());
                }
              }, 250);
            },
          },
        });
      })
      .catch(() => { /* the embed just stays a plain player without a clock */ });

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
      playerRef.current = null;
      if (mount.parentNode) mount.parentNode.removeChild(mount);
    };
  }, [videoId]);

  // Everything inside this container is managed by hand, never by React.
  return <div className="gs-sing__video" ref={hostRef} title={title} />;
}
